import React, { ReactElement } from 'react';
import styled, { useTheme } from 'styled-components';

import { hexToRGBA } from 'src/common/helpers';

import { Info, Tick } from '../icons';

type AlertType = 'success' | 'warning' | 'error' | 'info';

interface Props {
    type: AlertType;
    content: string | ReactElement;
    showIcon?: boolean;
}

const Wrapper = styled.div<{ type: AlertType }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
    ${({ theme }) => `
        line-height: ${theme.typography.lineHeight[6]};
    `};
    background: ${({ theme: { colors }, type }) => {
        switch (type) {
            case 'success':
                return `${hexToRGBA(colors.alert.green, 0.15)}`;
            case 'warning':
                return `${hexToRGBA(colors.alert.yellow, 0.15)}`;
            case 'error':
                return `${hexToRGBA(colors.alert.red, 0.15)}`;
            case 'info':
                return `${hexToRGBA(colors.alert.blue, 0.15)}`;
        }
    }};
`;

const IconWrapper = styled.div`
    flex-shrink: 0;
`;

const Alert: React.FC<Props> = ({ type, content, showIcon = true }) => {
    const { colors } = useTheme();
    const renderIcon = () => {
        switch (type) {
            case 'success':
                return <Tick stroke={colors.alert.green} />;
            case 'warning':
                return <Info fill={colors.alert.yellow} />;
            case 'error':
                return <Info fill={colors.alert.red} />;
            case 'info':
                return <Info fill={colors.alert.blue} />;
        }
    };

    return (
        <Wrapper type={type}>
            {showIcon && <IconWrapper>{renderIcon()}</IconWrapper>}
            <>{content}</>
        </Wrapper>
    );
};

export { Alert };
